import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllUsers, getDistributorOrganizations, changeRole, changeStatus, registerUser } from '../../apis/userAPI';
import { deleteToken } from '../../apis/sessionTokenAPI';
import { getAuditLogs } from '../../apis/auditLogAPI';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HistoryIcon from '@mui/icons-material/History';
import './adminUserManagePage.css';
import SidebarNav from '../../components/SidebarNav';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import {
  canViewUserActivity,
  createUserRows,
  DEFAULT_USER_SORT,
  filterUsersByGroup,
  filterUsersByStatus,
  getNextSort,
  isUserActive,
  sortUserRows,
  USER_GROUPS,
} from './userListUtils';

const AUDIT_ACTION_LABELS = {
  loginSuccess: 'Login',
  loginFailure: 'Failed login',
  accountLocked: 'Account locked',
  logout: 'Logout',
  userRegistered: 'User registered',
  userActivated: 'Activated',
  userDeactivated: 'Deactivated',
  roleChanged: 'Role changed',
  passwordChanged: 'Password changed',
};

const formatAuditAction = (action) => AUDIT_ACTION_LABELS[action] || action;

const formatAuditTimestamp = (value) => (value ? new Date(value).toLocaleString() : '—');

const formatAuditDetail = (detail) => {
  if (!detail || typeof detail !== 'object' || Object.keys(detail).length === 0) return '—';
  return Object.entries(detail).map(([key, value]) => `${key}: ${value}`).join(', ');
};

const SORTABLE_COLUMNS = [
  { key: 'email', label: 'Email' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'role', label: 'Role' },
  { key: 'organization', label: 'Organization' },
  { key: 'subscription', label: 'Subscription' },
  { key: 'status', label: 'Status' },
];

const AdminUserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [activeUserGroup, setActiveUserGroup] = useState(USER_GROUPS.CAPSOVISION);
  const [accountStatusFilter, setAccountStatusFilter] = useState('active');
  const [statusChangeUser, setStatusChangeUser] = useState(null);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [passwordChangeUser, setPasswordChangeUser] = useState(null);
  const [activityUser, setActivityUser] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [sort, setSort] = useState(DEFAULT_USER_SORT);
  const [newUser, setNewUser] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'reviewer',
    password: '',
    organizationId: '',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = location.state;
  const isSiteAdmin = currentUser.role === 'admin';
  const isDistributorAdmin = currentUser.role === 'distributorAdmin';
  const isDistributorRole = (role) => ['distributorAdmin', 'distributorRegular'].includes(role);
  const groupUsers = isSiteAdmin ? filterUsersByGroup(users, activeUserGroup) : users;
  const visibleUsers = filterUsersByStatus(groupUsers, accountStatusFilter);
  const sortedUserRows = sortUserRows(createUserRows(visibleUsers, organizations), sort);

  const renderSortableHeader = ({ key, label }) => {
    const direction = sort.key === key ? sort.direction : null;
    return (
      <th key={key} aria-sort={direction ? `${direction}ending` : 'none'}>
        <button
          type="button"
          className={`admin-sort-button${direction ? ' is-sorted' : ''}`}
          onClick={() => setSort((currentSort) => getNextSort(currentSort, key))}
          aria-label={`Sort by ${label}${direction ? `, currently ${direction}ending` : ''}`}
        >
          <span>{label}</span>
          <span className="admin-sort-indicator" aria-hidden="true">
            {direction === 'desc'
              ? <ArrowDownwardIcon fontSize="small" />
              : <ArrowUpwardIcon fontSize="small" />}
          </span>
        </button>
      </th>
    );
  };

  const fetchUsers = async () => {
    try {
      const data = await getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('There was an error fetching the users!', error);
      alert('Failed to fetch users: ' + (error.message || 'Unknown error'));
    }
  };

  useEffect(() => {
    fetchUsers();
    if (isDistributorAdmin) {
      setNewUser((previous) => ({ ...previous, role: 'distributorRegular' }));
    }
    if (isSiteAdmin) {
      getDistributorOrganizations()
        .then(setOrganizations)
        .catch((error) => alert('Failed to load CapsoCloud distributors: ' + (error.message || 'Unknown error')));
    }
  }, [isSiteAdmin, isDistributorAdmin]);

  // Call backend changeRole to change the role of a user (admin or reviewer)
  const handleChangeRole = async (email, newRole) => {
    try {
      const user = users.find((entry) => entry.email === email);
      await changeRole({ email, newRole, organizationId: user?.organizationId });
      const updatedUsers = users.map(user => 
        user.email === email ? { ...user, role: newRole } : user
      );
      setUsers(updatedUsers);
      alert('User role changed successfully');
    } catch (error) {
      console.error('There was an error changing the user role!', error);
      alert('Failed to change user role: ' + error.message);
    }
  };

  const handleChangeStatus = async () => {
    if (!statusChangeUser) return;
    const nextIsActive = !isUserActive(statusChangeUser);
    setIsChangingStatus(true);
    try {
      await changeStatus({ email: statusChangeUser.email, isActive: nextIsActive });
      setUsers((currentUsers) => currentUsers.map((user) => (
        user.email === statusChangeUser.email ? { ...user, isActive: nextIsActive } : user
      )));
      setStatusChangeUser(null);
      alert(`User account ${nextIsActive ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('There was an error changing the user account status!', error);
      alert('Failed to change user account status: ' + (error.message || 'Unknown error'));
    } finally {
      setIsChangingStatus(false);
    }
  };

  const handleViewActivity = async (user) => {
    setActivityUser(user);
    setActivityLogs([]);
    setActivityError('');
    setIsLoadingActivity(true);
    try {
      const data = await getAuditLogs({ userEmail: user.email, limit: 100 });
      setActivityLogs(data.rows || []);
    } catch (error) {
      console.error('There was an error fetching user activity!', error);
      setActivityError(error.message || 'Failed to load activity');
    } finally {
      setIsLoadingActivity(false);
    }
  };

  const handleNewUserChange = (field, value) => {
    setNewUser((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await registerUser(newUser);
      alert('User added successfully');
      setNewUser({
        email: '',
        first_name: '',
        last_name: '',
        role: isDistributorAdmin ? 'distributorRegular' : 'reviewer',
        password: '',
        organizationId: '',
      });
      setShowAddUserForm(false);
      await fetchUsers();
    } catch (error) {
      console.error('There was an error adding the user!', error);
      alert('Failed to add user: ' + (error.message || 'Unknown error'));
    }
  };

  const handleHome = () => {
    navigate('/main', { state: { currentUser } });
  };

  const handleCharts = () => {
    navigate('/charts', { state: { currentUser } });
  };

  const handleTools = () => {
    navigate('/tools', { state: { currentUser } });
  };

  const handleLogout = async () => {
    await deleteToken();
    navigate('/login');
  };

  return (
    <div className="main-page-container admin-users-page">
      <SidebarNav
        activePage="users"
        onHome={handleHome}
        onCharts={handleCharts}
        onTools={handleTools}
        onLogout={handleLogout}
      />

      {/* Main content */}
      <div className="main-content">
        {/* Header */}
        <header className="header">
          <div>
            <h2 className="text-lg font-semibold">Welcome, {currentUser.first_name}</h2>
          </div>
        </header>

        {/* Page content */}
        <div className="container space-y-6">
          <div className="admin-users-toolbar">
            <h2 className="page-title">Users</h2>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={() => setShowAddUserForm(true)}
            >
              Add User
            </button>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">User Administration</h3>
            </div>
            <div className="admin-card-description">
              {isDistributorAdmin
                ? 'Manage users for your distributor organization.'
                : 'Manage official users and distributor accounts linked to CapsoCloud organizations.'}
            </div>
          </div>

          {/* User Table */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                {isSiteAdmin && activeUserGroup === USER_GROUPS.DISTRIBUTOR ? 'Distributor Users' : 'CapsoVision Users'}
              </h3>
              <div className="admin-status-switch" role="group" aria-label="Filter users by account status">
                <button
                  type="button"
                  className={`admin-status-switch-button${accountStatusFilter === 'active' ? ' active' : ''}`}
                  aria-pressed={accountStatusFilter === 'active'}
                  onClick={() => setAccountStatusFilter('active')}
                >
                  Active
                </button>
                <button
                  type="button"
                  className={`admin-status-switch-button inactive${accountStatusFilter === 'inactive' ? ' active' : ''}`}
                  aria-pressed={accountStatusFilter === 'inactive'}
                  onClick={() => setAccountStatusFilter('inactive')}
                >
                  Inactive
                </button>
              </div>
            </div>
            {isSiteAdmin && (
              <div className="admin-user-tabs" role="tablist" aria-label="User groups">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeUserGroup === USER_GROUPS.CAPSOVISION}
                  className={`admin-user-tab${activeUserGroup === USER_GROUPS.CAPSOVISION ? ' active' : ''}`}
                  onClick={() => setActiveUserGroup(USER_GROUPS.CAPSOVISION)}
                >
                  CapsoVision ({filterUsersByGroup(users, USER_GROUPS.CAPSOVISION).length})
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeUserGroup === USER_GROUPS.DISTRIBUTOR}
                  className={`admin-user-tab${activeUserGroup === USER_GROUPS.DISTRIBUTOR ? ' active' : ''}`}
                  onClick={() => setActiveUserGroup(USER_GROUPS.DISTRIBUTOR)}
                >
                  Distributor ({filterUsersByGroup(users, USER_GROUPS.DISTRIBUTOR).length})
                </button>
              </div>
            )}
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    {SORTABLE_COLUMNS.map(renderSortableHeader)}
                    <th>Actions</th>
                    <th>Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUserRows.map(({ user, email, firstName, lastName, role, organization, subscription, status }) => (
                    <tr key={user.id || user.email}>
                      <td>{email}</td>
                      <td>{firstName}</td>
                      <td>{lastName}</td>
                      <td>{role}</td>
                      <td>{organization}</td>
                      <td>{subscription}</td>
                      <td>
                        <span className={`admin-status-badge ${status.toLowerCase()}`}>{status}</span>
                      </td>
                      <td>
                        <div className="button-box">
                          {isSiteAdmin && (
                            <button
                              type="button"
                              className="admin-btn admin-btn-primary"
                              onClick={() => handleChangeRole(
                                user.email,
                                user.role === 'admin'
                                  ? 'reviewer'
                                  : user.role === 'reviewer'
                                    ? 'admin'
                                    : user.role === 'distributorAdmin'
                                      ? 'distributorRegular'
                                      : 'distributorAdmin'
                              )}
                            >
                              {['admin', 'distributorAdmin'].includes(user.role) ? 'Demote to Regular' : 'Promote to Admin'}
                            </button>
                          )}
                          <button
                            type="button"
                            className="admin-btn admin-btn-primary"
                            onClick={() => setPasswordChangeUser(user)}
                          >
                            Change Password
                          </button>
                          {(isSiteAdmin || user.role === 'distributorRegular') && (
                            <button
                              type="button"
                              className={`admin-btn ${isUserActive(user) ? 'admin-btn-destructive' : 'admin-btn-primary'}`}
                              disabled={user.email === currentUser.email && isUserActive(user)}
                              title={user.email === currentUser.email && isUserActive(user) ? 'You cannot deactivate your own account' : undefined}
                              onClick={() => setStatusChangeUser(user)}
                            >
                              {isUserActive(user) ? 'Deactivate' : 'Activate'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        {canViewUserActivity(currentUser, user) && (
                          <button
                            type="button"
                            className="admin-btn admin-btn-info admin-icon-btn"
                            aria-label="View Activity"
                            title="View Activity"
                            onClick={() => handleViewActivity(user)}
                          >
                            <HistoryIcon fontSize="small" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {visibleUsers.length === 0 && (
                    <tr>
                      <td colSpan="9" className="admin-users-empty">
                        No {accountStatusFilter} users in this group.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showAddUserForm && (
        <div
          className="admin-modal-overlay"
          role="presentation"
        >
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-user-modal-title"
          >
            <div className="admin-modal-header">
              <div>
                <h3 id="add-user-modal-title" className="admin-modal-title">Add User</h3>
                <p className="admin-modal-subtitle">Create an invited account with an initial password.</p>
              </div>
            </div>

            <form onSubmit={handleAddUser} className="admin-modal-form">
              <div className="admin-form-grid">
                <label className="admin-form-field">
                  <span>Email</span>
                  <input
                    type="email"
                    placeholder="user@example.com"
                    value={newUser.email}
                    onChange={(e) => handleNewUserChange('email', e.target.value)}
                    required
                  />
                </label>
                <label className="admin-form-field">
                  <span>First Name</span>
                  <input
                    type="text"
                    placeholder="First name"
                    value={newUser.first_name}
                    onChange={(e) => handleNewUserChange('first_name', e.target.value)}
                    required
                  />
                </label>
                <label className="admin-form-field">
                  <span>Last Name</span>
                  <input
                    type="text"
                    placeholder="Last name"
                    value={newUser.last_name}
                    onChange={(e) => handleNewUserChange('last_name', e.target.value)}
                    required
                  />
                </label>
                <label className="admin-form-field">
                  <span>Role</span>
                  <select
                    value={newUser.role}
                    onChange={(e) => handleNewUserChange('role', e.target.value)}
                    required
                  >
                    {isSiteAdmin ? (
                      <>
                        <option value="reviewer">Reviewer</option>
                        <option value="admin">Site Admin</option>
                        <option value="distributorRegular">Distributor Regular</option>
                        <option value="distributorAdmin">Distributor Admin</option>
                      </>
                    ) : (
                      <>
                        <option value="distributorRegular">Distributor Regular</option>
                        <option value="distributorAdmin">Distributor Admin</option>
                      </>
                    )}
                  </select>
                </label>
                {isSiteAdmin && isDistributorRole(newUser.role) && (
                  <label className="admin-form-field admin-form-field-full">
                    <span>CapsoCloud Distributor</span>
                    <select
                      value={newUser.organizationId}
                      onChange={(e) => handleNewUserChange('organizationId', e.target.value)}
                      required
                    >
                      <option value="">Select a distributor</option>
                      {organizations.map((organization) => (
                        <option key={organization.id} value={organization.id}>{organization.name}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="admin-form-field admin-form-field-full">
                  <span>Password</span>
                  <input
                    type="password"
                    placeholder="Temporary password"
                    value={newUser.password}
                    onChange={(e) => handleNewUserChange('password', e.target.value)}
                    required
                  />
                </label>
              </div>

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  onClick={() => setShowAddUserForm(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {statusChangeUser && (
        <div className="admin-modal-overlay" role="presentation">
          <div
            className="admin-modal admin-confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="status-confirm-modal-title"
            aria-describedby="status-confirm-modal-description"
          >
            <div className="admin-modal-header">
              <div>
                <h3 id="status-confirm-modal-title" className="admin-modal-title">
                  Confirm {isUserActive(statusChangeUser) ? 'Deactivation' : 'Activation'}
                </h3>
              </div>
            </div>
            <div className="admin-confirm-modal-body">
              <p id="status-confirm-modal-description">
                Are you sure you want to {isUserActive(statusChangeUser) ? 'deactivate' : 'activate'} the{' '}
                {statusChangeUser.role} user{' '}
                {statusChangeUser.firstName ?? statusChangeUser.first_name}{' '}
                {statusChangeUser.lastName ?? statusChangeUser.last_name} with email {statusChangeUser.email}?
              </p>
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary"
                  disabled={isChangingStatus}
                  onClick={() => setStatusChangeUser(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`admin-btn ${isUserActive(statusChangeUser) ? 'admin-btn-destructive' : 'admin-btn-primary'}`}
                  disabled={isChangingStatus}
                  onClick={handleChangeStatus}
                >
                  {isChangingStatus
                    ? 'Updating...'
                    : isUserActive(statusChangeUser) ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ChangePasswordModal
        open={!!passwordChangeUser}
        targetEmail={passwordChangeUser?.email}
        onClose={() => setPasswordChangeUser(null)}
        onSuccess={() => {
          setPasswordChangeUser(null);
          alert('Password changed successfully');
        }}
      />

      {activityUser && (
        <div className="admin-modal-overlay" role="presentation">
          <div
            className="admin-modal admin-activity-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="activity-modal-title"
          >
            <div className="admin-modal-header">
              <div>
                <h3 id="activity-modal-title" className="admin-modal-title">Activity</h3>
                <p className="admin-modal-subtitle">
                  Recent activity for {activityUser.firstName ?? activityUser.first_name}{' '}
                  {activityUser.lastName ?? activityUser.last_name} ({activityUser.email})
                </p>
              </div>
            </div>
            <div className="admin-activity-modal-body">
              {isLoadingActivity && <p>Loading activity...</p>}
              {!isLoadingActivity && activityError && <p className="form-error">{activityError}</p>}
              {!isLoadingActivity && !activityError && (
                <div className="table-container admin-activity-table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date/Time</th>
                        <th>Action</th>
                        <th>Actor</th>
                        <th>Target</th>
                        <th>IP Address</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityLogs.map((log) => (
                        <tr key={log.id}>
                          <td>{formatAuditTimestamp(log.createdAt)}</td>
                          <td>{formatAuditAction(log.action)}</td>
                          <td>{log.actorEmail || '—'}</td>
                          <td>{log.targetEmail || '—'}</td>
                          <td>{log.ipAddress || '—'}</td>
                          <td>{formatAuditDetail(log.detail)}</td>
                        </tr>
                      ))}
                      {activityLogs.length === 0 && (
                        <tr>
                          <td colSpan="6" className="admin-users-empty">No activity recorded yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="admin-modal-actions">
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => setActivityUser(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagementPage;
